import React, { useState, useEffect, useRef } from 'react';
import {
  X, GitPullRequest, AlertCircle, Code, FileCode,
  Link2, Users, Clock, ArrowRight, Lightbulb,
  ChevronDown, ChevronRight, ExternalLink, Loader2,
  GitMerge, GitBranch, File, Plus, Minus, ZoomIn, ZoomOut,
  Play, CheckCircle, XCircle
} from 'lucide-react';

// Simple force-directed graph visualization using canvas
function GraphVisualization({ graph, width = 600, height = 400 }) {
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [hoveredNode, setHoveredNode] = useState(null);

  useEffect(() => {
    if (!graph || !graph.nodes || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Node positions (simple circular layout)
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 3;

    const nodePositions = {};
    const centralNode = graph.central_node;

    // Place central node at center
    if (centralNode) {
      nodePositions[centralNode] = { x: centerX, y: centerY };
    }

    // Place other nodes in a circle
    const otherNodes = graph.nodes.filter(n => n.id !== centralNode);
    otherNodes.forEach((node, i) => {
      const angle = (2 * Math.PI * i) / otherNodes.length;
      nodePositions[node.id] = {
        x: centerX + radius * Math.cos(angle) * zoom,
        y: centerY + radius * Math.sin(angle) * zoom
      };
    });

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw edges
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    graph.edges.forEach(edge => {
      const source = nodePositions[edge.source];
      const target = nodePositions[edge.target];
      if (source && target) {
        ctx.beginPath();
        ctx.moveTo(source.x, source.y);
        ctx.lineTo(target.x, target.y);
        ctx.stroke();

        // Draw edge label
        const midX = (source.x + target.x) / 2;
        const midY = (source.y + target.y) / 2;
        ctx.font = '9px sans-serif';
        ctx.fillStyle = '#9ca3af';
        ctx.fillText(edge.label, midX, midY);
      }
    });

    // Draw nodes
    graph.nodes.forEach(node => {
      const pos = nodePositions[node.id];
      if (!pos) return;

      const isHovered = hoveredNode === node.id;
      const isCentral = node.id === centralNode;
      const nodeRadius = isCentral ? 25 : 18;

      // Node color based on type
      let color = '#6b7280';
      if (node.type === 'pull_request') color = '#8b5cf6';
      else if (node.type === 'issue') color = '#22c55e';
      else if (node.type === 'file') color = '#f59e0b';
      else if (node.type === 'code') color = '#3b82f6';
      else if (node.type === 'workflow_run') color = '#f97316';
      else if (node.type === 'job') color = '#84cc16';

      // Draw node circle
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, nodeRadius, 0, 2 * Math.PI);
      ctx.fillStyle = isHovered ? color : `${color}dd`;
      ctx.fill();

      if (isCentral) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.stroke();
      }

      // Draw label
      ctx.font = isCentral ? 'bold 10px sans-serif' : '9px sans-serif';
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const label = node.label?.substring(0, 12) || '';
      ctx.fillText(label, pos.x, pos.y);
    });

  }, [graph, zoom, hoveredNode, width, height]);

  if (!graph || !graph.nodes || graph.nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-400">No graph data available</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute top-2 right-2 flex gap-1 z-10">
        <button
          onClick={() => setZoom(z => Math.min(z + 0.2, 2))}
          className="p-1 bg-white border border-gray-200 rounded hover:bg-gray-50"
          title="Zoom in"
        >
          <ZoomIn className="w-4 h-4 text-gray-600" />
        </button>
        <button
          onClick={() => setZoom(z => Math.max(z - 0.2, 0.5))}
          className="p-1 bg-white border border-gray-200 rounded hover:bg-gray-50"
          title="Zoom out"
        >
          <ZoomOut className="w-4 h-4 text-gray-600" />
        </button>
      </div>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="border border-gray-200 rounded-lg bg-white"
      />
      <div className="flex flex-wrap justify-center gap-4 mt-2 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-purple-500"></span> PR
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-green-500"></span> Issue
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-amber-500"></span> File
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-blue-500"></span> Code
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-orange-500"></span> Workflow
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-lime-500"></span> Job
        </span>
      </div>
    </div>
  );
}

// Code changes component
function CodeChanges({ changes }) {
  const [expanded, setExpanded] = useState(false);

  if (!changes || changes.length === 0) {
    return null;
  }

  const totalAdditions = changes.reduce((sum, c) => sum + (c.additions || 0), 0);
  const totalDeletions = changes.reduce((sum, c) => sum + (c.deletions || 0), 0);

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <FileCode className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-sm">Code Changes</span>
          <span className="text-xs text-gray-500">
            ({changes.length} file{changes.length !== 1 ? 's' : ''})
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-green-600">+{totalAdditions}</span>
          <span className="text-xs text-red-600">-{totalDeletions}</span>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </button>
      {expanded && (
        <div className="border-t border-gray-200 max-h-64 overflow-y-auto">
          {changes.map((change, idx) => (
            <div key={idx} className="px-4 py-2 border-b border-gray-100 last:border-0 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <File className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-sm text-gray-700 font-mono">{change.file_path}</span>
                {change.status && change.status !== 'summary' && (
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    change.status === 'added' ? 'bg-green-100 text-green-700' :
                    change.status === 'removed' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {change.status}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-green-600 flex items-center gap-0.5">
                  <Plus className="w-3 h-3" />{change.additions}
                </span>
                <span className="text-red-600 flex items-center gap-0.5">
                  <Minus className="w-3 h-3" />{change.deletions}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Related items component
function RelatedItems({ items }) {
  const [expanded, setExpanded] = useState(true);

  if (!items || items.length === 0) {
    return null;
  }

  const getIcon = (type) => {
    if (type === 'pull_request') return <GitPullRequest className="w-3.5 h-3.5 text-purple-500" />;
    if (type === 'issue') return <AlertCircle className="w-3.5 h-3.5 text-green-500" />;
    if (type === 'code') return <Code className="w-3.5 h-3.5 text-blue-500" />;
    if (type === 'workflow_run') return <Play className="w-3.5 h-3.5 text-orange-500" />;
    return <Link2 className="w-3.5 h-3.5 text-gray-500" />;
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-sm">Related Items</span>
          <span className="text-xs text-gray-500">({items.length})</span>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {expanded && (
        <div className="border-t border-gray-200 max-h-64 overflow-y-auto">
          {items.map((item, idx) => (
            <div key={idx} className="px-4 py-2 border-b border-gray-100 last:border-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 flex-1">
                  {getIcon(item.type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 truncate">{item.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        item.relationship === 'linked' ? 'bg-blue-100 text-blue-700' :
                        item.relationship === 'same_author' ? 'bg-purple-100 text-purple-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {item.relationship}
                      </span>
                      <span className="text-xs text-gray-400">
                        {Math.round(item.relevance_score * 100)}% match
                      </span>
                    </div>
                  </div>
                </div>
                {item.url && (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Main Investigation Modal
export default function InvestigationModal({ isOpen, onClose, item, itemType = 'pr' }) {
  const [loading, setLoading] = useState(false);
  const [investigation, setInvestigation] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (isOpen && item) {
      loadInvestigation();
    }
    // Reset state when modal closes
    if (!isOpen) {
      setInvestigation(null);
      setError(null);
    }
  }, [isOpen, item, itemType]);

  const loadInvestigation = async () => {
    if (!item) {
      setError('No item selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated. Please log in again.');
      }

      let endpoint = '/api/investigate/pr';

      if (itemType === 'issue') {
        endpoint = '/api/investigate/issue';
      } else if (itemType === 'code') {
        endpoint = '/api/investigate/code';
      } else if (itemType === 'workflow_run') {
        endpoint = '/api/investigate/workflow-run';
      }

      console.log('Investigating item:', item.id, 'type:', itemType, 'endpoint:', endpoint);

      const body = {
        pr_id: item.id,
        issue_id: item.id,
        entity_id: item.id,
        run_id: item.id,
        repo_key: item.repo || item.repo_key
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Investigation failed: ${response.status}`);
      }

      const data = await response.json();
      setInvestigation(data.investigation);
    } catch (err) {
      console.error('Investigation error:', err);
      setError(err.message || 'Failed to investigate item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getTypeIcon = () => {
    if (itemType === 'issue') return <AlertCircle className="w-5 h-5 text-green-500" />;
    if (itemType === 'code') return <Code className="w-5 h-5 text-blue-500" />;
    if (itemType === 'workflow_run') return <Play className="w-5 h-5 text-orange-500" />;
    return <GitPullRequest className="w-5 h-5 text-purple-500" />;
  };

  const getTypeLabel = () => {
    if (itemType === 'issue') return 'Issue';
    if (itemType === 'code') return 'Code';
    if (itemType === 'workflow_run') return 'Workflow Run';
    return 'Pull Request';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-6">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getTypeIcon()}
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  {loading ? 'Loading...' : investigation?.title || item?.title || 'Investigation'}
                </h2>
                <p className="text-sm text-gray-500">
                  {getTypeLabel()} Investigation
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
            {loading && (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                <span className="ml-3 text-gray-600">Investigating...</span>
              </div>
            )}

            {error && (
              <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700">{error}</p>
                  <button
                    onClick={loadInvestigation}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}

            {investigation && !loading && (
              <div className="p-6 space-y-6">
                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700">{investigation.summary}</p>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200">
                  <nav className="flex gap-4">
                    {['overview', 'graph', 'related', 'insights'].map(tab => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                          activeTab === tab
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-4">
                    {/* Metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {investigation.metrics && Object.entries(investigation.metrics).map(([key, value]) => (
                        <div key={key} className="bg-white border border-gray-200 rounded-lg p-3">
                          <p className="text-xs text-gray-500 uppercase">{key.replace(/_/g, ' ')}</p>
                          <p className="text-xl font-semibold text-gray-800">{value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Details */}
                    {investigation.details && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h3 className="text-sm font-medium text-gray-800 mb-3">Details</h3>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {Object.entries(investigation.details).map(([key, value]) => {
                            if (!value || key === 'body') return null;
                            return (
                              <div key={key} className="flex">
                                <span className="text-gray-500 w-24 flex-shrink-0">{key.replace(/_/g, ' ')}:</span>
                                <span className="text-gray-800 truncate">
                                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Code Changes */}
                    <CodeChanges changes={investigation.code_changes} />
                  </div>
                )}

                {/* Graph Tab */}
                {activeTab === 'graph' && (
                  <div>
                    <GraphVisualization graph={investigation.graph} width={700} height={400} />
                    <p className="text-xs text-gray-400 text-center mt-2">
                      Showing {investigation.graph?.nodes?.length || 0} nodes and {investigation.graph?.edges?.length || 0} relationships
                    </p>
                  </div>
                )}

                {/* Related Tab */}
                {activeTab === 'related' && (
                  <RelatedItems items={investigation.related_items} />
                )}

                {/* Insights Tab */}
                {activeTab === 'insights' && (
                  <div className="space-y-3">
                    {investigation.insights?.map((insight, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <Lightbulb className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-yellow-800">{insight}</p>
                      </div>
                    ))}
                    {(!investigation.insights || investigation.insights.length === 0) && (
                      <p className="text-gray-500 text-center py-8">No insights available</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
            <p className="text-xs text-gray-400">
              {investigation?.investigated_at && `Investigated at ${new Date(investigation.investigated_at).toLocaleString()}`}
            </p>
            <div className="flex items-center gap-2">
              {item?.html_url && (
                <a
                  href={item.html_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
                >
                  <ExternalLink className="w-4 h-4" />
                  View on GitHub
                </a>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
